import React, { useState, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { Select } from "@narmi/design_system";
import { DateTime } from "luxon";
import { entities } from "byzantine";
import Payee from "byzantine/src/Payee";
import AddPayeeDrawer from "./AddPayeeDrawer";
import utils from "../../utils";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import styles from "./PayeeSelector.module.scss";

type PayeeSelectorProps = {
  payees: Payee[];
  value?: string;
  field?: string;
  onChange: (payeeId: string) => void;
  updatePayees: (payee: Payee) => void;
  setMinDate: (date: string) => void;
  setDisabledDates: React.Dispatch<
    React.SetStateAction<(Date | ((date: Date) => boolean))[]>
  >;
  updateDatepickerValue: (date: string) => void;
  label?: string;
};

const PayeeDropdownItem = ({ payee }: { payee: Payee }) => {
  // While our new Payee form requires a nickname, in the event that billpay doesn't require a nickname to be
  // defined, or it's entered as an empty string we can fallback to the name only.
  const nickname = payee.description || payee.getDisplayName();
  return (
    <div className={styles["payee-select"]}>
      <span>{nickname}</span>
      {/* If the payee has a nickname, show it as a subheading */}
      {payee.description ? (
        <span className={styles["payee-select-subheading"]}>
          {payee.getDisplayName()}
        </span>
      ) : null}
    </div>
  );
};

export default function PayeeSelector({
  payees,
  value,
  onChange,
  updatePayees,
  setMinDate,
  setDisabledDates,
  updateDatepickerValue,
  label,
}: PayeeSelectorProps) {
  const { l10n } = useLocalization();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPayee, setSelectedPayee] = useState<Payee | null>(null);
  const { currentUser } = useCurrentUser();
  const { send: fetchHolidays, hasData: hasHolidays } =
    entities.holidays.useFetchHolidays();
  // holiday dates as "yyyy-MM-dd" strings
  const holidays = entities.holidays.useHolidays();

  useEffect(() => {
    if (!hasHolidays) {
      fetchHolidays();
    }
  }, [fetchHolidays, hasHolidays]);

  useEffect(() => {
    if (value) {
      const matchingPayees = payees.filter((payee) => payee.id === value);
      if (matchingPayees.length > 0) {
        setSelectedPayee(matchingPayees[0]);
      }
    }
  }, [value, payees]);

  useEffect(() => {
    const disabled: (Date | ((date: Date) => boolean))[] = holidays.map(
      (holiday) => DateTime.fromISO(holiday).toJSDate()
    );
    // also disable weekends
    disabled.push((date: Date) => [0, 6].includes(date.getDay()));
    setDisabledDates(disabled);
  }, [holidays, setDisabledDates]);

  useEffect(() => {
    let minDate = DateTime.now().toFormat("MM/dd/yyyy");
    /* set the minDate based on the selected Payee */
    if (
      selectedPayee &&
      selectedPayee.delivery_options &&
      selectedPayee.delivery_options.length > 0
    ) {
      const minDateForPayee = DateTime.fromISO(
        selectedPayee.delivery_options[0].date
      ).toJSDate();
      /* minDate can't be earlier than today */
      if (minDateForPayee > utils.localIsoDate()) {
        minDate = DateTime.fromJSDate(minDateForPayee).toFormat("MM/dd/yyyy");
        setMinDate(minDate);
      }
      updateDatepickerValue(minDate);
    }
  }, [selectedPayee]);

  return (
    <>
      <Select
        id="payeeSelector"
        label={label}
        onChange={onChange}
        value={value}
      >
        {payees.map((payee) => (
          <Select.Item key={payee.id} value={payee.id} searchValue={payee.name}>
            <PayeeDropdownItem payee={payee} />
          </Select.Item>
        ))}
        {(currentUser?.features?.["integrations.base.Payee_permission"] ||
          !currentUser?.isBusiness()) && (
          <Select.Action onSelect={() => setModalOpen(true)}>
            <span className="fontColor--pine fontWeight--bold">
              <span className="narmi-icon-plus padding--right--xs" />
              {l10n.getString("button-link-payee", null, "Link a new payee")}
            </span>
          </Select.Action>
        )}
      </Select>
      <AddPayeeDrawer
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        updatePayees={updatePayees}
      />
    </>
  );
}
